import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardList, Plus, Check, Trash2, Clock, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const PRIORITIES = {
  basse: "bg-slate-100 text-slate-700",
  normale: "bg-blue-100 text-blue-700",
  haute: "bg-amber-100 text-amber-800",
  urgente: "bg-rose-100 text-rose-800",
};
const STATUTS = {
  a_faire: "bg-slate-100 text-slate-700",
  en_cours: "bg-blue-100 text-blue-700",
  terminee: "bg-emerald-100 text-emerald-700",
  annulee: "bg-slate-100 text-slate-500 line-through",
};
const CATEGORIES = ["", "kyc", "adhesion", "convention", "finance", "autre"];

export default function Tasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "gestionnaire_ge";
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterStatut, setFilterStatut] = useState("");
  const [viewMode, setViewMode] = useState(isAdmin ? "all" : "mine"); // all | mine
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ titre: "", description: "", assignee_id: "", priorite: "normale", categorie: "autre", echeance: "" });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterStatut) params.statut = filterStatut;
      if (viewMode === "mine") params.mine = true;
      const { data } = await api.get("/tasks", { params });
      setTasks(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur chargement");
    }
  }, [filterStatut, viewMode]);

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/auth/users").catch(() => ({ data: [] }));
      setUsers(data || []);
    } catch { /* ignore */ }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const create = async () => {
    if (!form.titre || !form.assignee_id) { toast.error("Titre et destinataire requis"); return; }
    try {
      await api.post("/tasks", { ...form, echeance: form.echeance || null });
      toast.success("Tâche créée et notifiée");
      setNewOpen(false);
      setForm({ titre: "", description: "", assignee_id: "", priorite: "normale", categorie: "autre", echeance: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur création");
    }
  };

  const updateStatut = async (t, statut) => {
    try {
      await api.patch(`/tasks/${t.id}`, { statut });
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const remove = async (t) => {
    if (!window.confirm("Supprimer cette tâche ?")) return;
    try { await api.delete(`/tasks/${t.id}`); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const isOverdue = (t) => t.echeance && t.statut !== "terminee" && new Date(t.echeance) < new Date();

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <PageHeader
        title="Tâches assignées"
        subtitle={isAdmin ? "Créez des tâches et assignez-les aux membres de l'équipe." : "Les tâches qui vous ont été confiées."}
        icon={ClipboardList}
        actions={isAdmin && (
          <Button onClick={() => setNewOpen(true)} className="bg-[#C5A059] hover:bg-[#A88846]" data-testid="new-task-btn">
            <Plus className="h-4 w-4 mr-2" />Nouvelle tâche
          </Button>
        )}
      />

      <div className="flex gap-3 flex-wrap">
        {isAdmin && (
          <div className="flex gap-1 bg-white rounded-lg border border-slate-200 p-1">
            <button onClick={() => setViewMode("all")} className={`px-3 py-1.5 text-sm rounded ${viewMode === "all" ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="view-all">Toutes</button>
            <button onClick={() => setViewMode("mine")} className={`px-3 py-1.5 text-sm rounded ${viewMode === "mine" ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid="view-mine">Mes tâches</button>
          </div>
        )}
        <Select value={filterStatut} onValueChange={setFilterStatut}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Tous statuts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            {Object.keys(STATUTS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 border border-dashed border-slate-200 rounded-lg text-slate-500">
          Aucune tâche à afficher.
        </div>
      ) : (
        <div className="grid gap-3">
          {tasks.map(t => (
            <div key={t.id} className={`bg-white rounded-lg border p-4 ${isOverdue(t) ? "border-rose-300 bg-rose-50/30" : "border-slate-200"}`} data-testid={`task-${t.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={PRIORITIES[t.priorite]}>{t.priorite}</Badge>
                    <Badge className={STATUTS[t.statut]}>{t.statut.replace("_", " ")}</Badge>
                    {t.categorie && <Badge variant="outline" className="text-xs">{t.categorie}</Badge>}
                    {isOverdue(t) && <span className="text-xs text-rose-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3"/>En retard</span>}
                  </div>
                  <h4 className="font-semibold mt-1">{t.titre}</h4>
                  {t.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{t.description}</p>}
                  <div className="flex gap-3 text-xs text-slate-500 mt-2 flex-wrap">
                    <span>Assigné à : <strong>{t.assignee_name || t.assignee_email}</strong></span>
                    {t.echeance && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Échéance {new Date(t.echeance).toLocaleDateString("fr-FR")}</span>}
                    <span>Créée par {t.created_by_email}</span>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {t.statut === "a_faire" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatut(t, "en_cours")}>Démarrer</Button>
                  )}
                  {t.statut !== "terminee" && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => updateStatut(t, "terminee")} data-testid={`complete-${t.id}`}>
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-rose-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle tâche</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Titre *</label>
              <Input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} data-testid="task-titre" />
            </div>
            <div>
              <label className="text-xs font-medium">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
            <div>
              <label className="text-xs font-medium">Assigner à *</label>
              <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                <SelectTrigger data-testid="task-assignee"><SelectValue placeholder="Sélectionner un utilisateur" /></SelectTrigger>
                <SelectContent>
                  {users.length === 0 && <SelectItem value="placeholder" disabled>Aucun utilisateur trouvé</SelectItem>}
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email} ({u.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium">Priorité</label>
                <Select value={form.priorite} onValueChange={(v) => setForm({ ...form, priorite: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(PRIORITIES).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Catégorie</label>
                <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(Boolean).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Échéance</label>
                <Input type="date" value={form.echeance} onChange={(e) => setForm({ ...form, echeance: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Annuler</Button>
            <Button onClick={create} className="bg-[#C5A059] hover:bg-[#A88846]" data-testid="save-task-btn">Créer et notifier</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
